import { useEffect } from 'react';

import type { SegmentExports } from '@feathr/blackbox';

/** Interval between polling refetches while at least one row is in a non-terminal state. */
export const EXPORTS_POLL_INTERVAL_MS = 7_000;

/**
 * Drives polling for the exports dashboard table.
 *
 * While `hasActive` is true (i.e. the visible page has at least one row in
 * `pending` or `exporting`), this hook ticks every
 * {@link EXPORTS_POLL_INTERVAL_MS} and calls
 * `SegmentExports.refreshActiveListings()` to merge fresh BE state into the
 * existing model instances silently — no loading flicker, no row-clearing.
 *
 * Ticks are skipped while `document.visibilityState === 'hidden'` to avoid
 * background traffic. The interval is cleared when `hasActive` flips to false
 * or the consumer unmounts.
 *
 * `hasActive` is computed from the rows currently rendered on the page — if
 * the user paginates or filters away from in-progress rows, polling stops even
 * though server-side work continues. Polling resumes when an active row is
 * back in view (either by navigating back or via the next list refetch).
 *
 * @param segmentExports - The `SegmentExports` collection from the MobX store.
 * @param hasActive - Whether at least one visible row is in a non-terminal state.
 */
export function useExportsPolling(segmentExports: SegmentExports, hasActive: boolean): void {
  useEffect(() => {
    if (!hasActive) {
      return undefined;
    }

    function tick(): void {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      // `.catch(() => undefined)` is the codebase pattern (see
      // MetaIntegrationPage.hooks.ts) for fire-and-forget polling refetches —
      // satisfies the floating-promise lint rule without using `void`.
      segmentExports.refreshActiveListings().catch(() => undefined);
    }

    const intervalId = globalThis.setInterval(tick, EXPORTS_POLL_INTERVAL_MS);
    return (): void => {
      globalThis.clearInterval(intervalId);
    };
  }, [hasActive, segmentExports]);
}
